import type { ReactElement } from "react";
import { Link, useLocation, useParams } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { ApiState, titleFor } from "../../components/api-state.js";
import { FormSummary } from "../../forms/fields.js";
import { useApiForm } from "../../forms/use-api-form.js";
import { useSearchForm } from "../../forms/use-search-form.js";

import type { CenterContractorCandidate, CenterContractorChoice } from "./tenant-types.js";
import { adminCenterApi, adminCenterUrls, wideSpace } from "./urls.js";

/**
 * 最後の確認はブラウザの確認ダイアログで行う（重要な操作は最後にダイアログで確かめる。docs/01 §7）。
 * <b>相手の承諾を待たずにすぐ替わる</b>ことと、前の契約者の扱いを入れる。
 */
export const changeContractorDialog = (
    title: string, candidate: CenterContractorCandidate, contractorName: string,
): string =>
    `「${title}」の契約者を「${candidate.userName}（${candidate.email}）」に変更します。`
    + `相手の承諾を待たずにすぐ替わり、前の契約者「${contractorName}」は管理者として残ります。新旧の契約者にお知らせのメールが届きます。よろしいですか？`;

/** 1 人を新しい契約者にする。<b>送ると契約 1 件の画面へ戻る</b>（行き先はサーバが返す）。 */
function ChooseButton({ choice, candidate }: {
    readonly choice: CenterContractorChoice;
    readonly candidate: CenterContractorCandidate;
}): ReactElement {
    const form = useApiForm(
        adminCenterApi.changeContractor(choice.tenantId), undefined,
        changeContractorDialog(choice.title, candidate, choice.contractorName ?? ""));

    return (
        <form method="post" onSubmit={form.onSubmit} className="shrink-0">
            <FormSummary messages={form.summary} />
            <input type="hidden" name="email" value={candidate.email} />
            <button type="submit" className="btn-secondary btn-sm" disabled={form.busy}>この方を契約者にする</button>
        </form>
    );
}

/** 候補を絞り込む（メールアドレス・表示名）。<b>欄のフォーカスとスクロールは保つ</b>（docs/01 §8）。 */
function CandidateFilter({ choice }: { readonly choice: CenterContractorChoice }): ReactElement {
    const form = useSearchForm(adminCenterUrls.contractor(choice.tenantId), { keyword: choice.keyword ?? "" });

    return (
        <form method="get" {...form.props} className="mb-4 flex flex-wrap items-end gap-2">
            <div className="min-w-0 grow">
                <label className="form-label" htmlFor="keyword">新しい契約者を探す</label>
                <input
                    className="form-input" id="keyword" name="keyword" defaultValue={choice.keyword ?? ""}
                    autoComplete="off" placeholder="メールアドレス・表示名"
                />
            </div>
            {/* 送るボタンとクリアはひとまとまりにする（折り返すときも一緒に次の行へ落ちる。片方だけ別の行に分かれない） */}
            <div className="flex gap-2">
                <button type="submit" className="btn-primary">絞り込む</button>
                {/* **クリアは「条件の無い URL を開く」。**type="reset" は描いたときの値に戻るだけ */}
                <Link to={adminCenterUrls.contractor(choice.tenantId)} className="btn-secondary">クリア</Link>
            </div>
        </form>
    );
}

function Candidates({ choice }: { readonly choice: CenterContractorChoice }): ReactElement {
    if (choice.candidates.length === 0) {
        return (
            <div className="empty-state text-sm text-slate-500">
                {choice.keyword === null || choice.keyword === ""
                    ? "選べる方がいません。新しい契約者は、この契約の登録を済ませた利用者から選びます（招待中の方は選べません）。"
                    : "当たる方がいませんでした。"}
            </div>
        );
    }

    return (
        <>
            <ul className="divide-y divide-slate-200" data-candidates="">
                {choice.candidates.map((candidate) => (
                    <li key={candidate.email} className="flex items-center justify-between gap-3 py-3">
                        <div className="min-w-0">
                            <div className="truncate font-medium">{candidate.userName}</div>
                            <div className="truncate text-sm text-slate-500">{`${candidate.email}${wideSpace}${candidate.role}`}</div>
                        </div>
                        <ChooseButton choice={choice} candidate={candidate} />
                    </li>
                ))}
            </ul>
            {choice.hasMore
                ? <p className="mt-4 text-sm text-slate-500">ほかにも選べる方がいます。メールアドレスか表示名で絞り込んでください。</p>
                : null}
        </>
    );
}

/**
 * 管理センターから契約者を替える（Implem.Portal.Backend の docs/features/35）。
 * <b>誰を選べるかはサーバが決める</b>（登録を済ませた利用者だけ。招待中・今の契約者は出ない）。
 * 入口は契約 1 件の画面の利用者の区画（アカウント管理を持つ人だけ。<c>canChangeContractor</c>）。
 */
export function ContractorChangePage(): ReactElement | null {
    const { search } = useLocation();
    const { id = "" } = useParams();
    const data = useApiData<CenterContractorChoice>(adminCenterApi.contractor(id, search));
    usePageMeta(titleFor(data, "契約者の変更"));

    if (data.state !== "ready") {
        return <ApiState data={data} what="契約者の変更" />;
    }
    const choice = data.value;

    return (
        <div className="page max-w-3xl">
            <p className="mb-4"><Link to={adminCenterUrls.tenant(choice.tenantId)} className="btn-secondary btn-sm">管理センターの契約へ戻る</Link></p>
            <div className="panel">
                <h1 className="page-title">契約者の変更</h1>
                <p className="page-lead">{`${choice.title}${wideSpace}${choice.company}${wideSpace}契約 ID ${String(choice.tenantId)}`}</p>

                <dl className="mb-4">
                    <dt className="text-xs text-slate-500">今の契約者</dt>
                    <dd data-contractor="">
                        {choice.contractorEmail === null
                            ? <span className="badge-warning">契約者がいません</span>
                            : `${choice.contractorName ?? ""}（${choice.contractorEmail}）`}
                    </dd>
                </dl>

                <div className="alert-warning mb-4">
                    <strong>相手の承諾を待たずに、すぐ替わります。</strong>
                    前の契約者は管理者として残り、新旧の契約者にお知らせのメールが届きます。
                    ご契約者の情報（企業名・お名前・連絡先）・カード情報は変わりません。
                    新しい契約者に契約内容の変更・カード情報の変更で直してもらってください。
                </div>

                <CandidateFilter choice={choice} />
                <Candidates choice={choice} />
            </div>
        </div>
    );
}
